import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Gillco Group";
const SITE_URL =
  process.env.REACT_APP_SITE_URL;

const DEFAULT_DESC =
  "Gillco Group — Punjab's leading luxury real estate developer. Curating legacy spaces with modern design for over two decades.";

const DEFAULT_IMAGE =
   `${SITE_URL}/gillco-logo-original.png`;


const ROUTE_META = {
  "/about": {
    title: "Gillco Group Legacy | We Build Dreams",
    description: "For over 25 years, Gillco Group has crafted landmark communities, transforming land potential into exceptional residential, commercial, and industrial destinations.",
  },

  "/vision":{
    title:"Vision & Philosophy | Gillco Group",
    description:"Driven by innovation, integrity, and long-term value creation, Gillco Group envisions building thriving communities that shape the future of modern India."
  },
  "/leadership":{
    title:"Leadership & Team | Gillco Group",
    description:"Meet the leadership team behind Gillco Group's 25+ year legacy of excellence, guiding residential, commercial, and industrial developments with vision, integrity, and purpose."
  },
  "/journey":{
    title:"Gillco Group Journey | Building a Legacy Since 2001",
    description:"Explore Gillco Group's journey since 2001, marked by visionary leadership, landmark developments, and over 25 years of trust, growth, and excellence."
  },
  "/awards":{
    title:"Awards & Recognition | Gillco Group",
    description:"Gillco Group's achievements and recognitions showcase over 25 years of dedication to creating landmark residential, commercial, and industrial developments."
  },
  "/projects":{
    title:"Our Projects | Gillco Group",
    description:"Discover Gillco Group's landmark projects across Punjab and the Tricity, featuring premium residential, commercial, and industrial developments."
  },
  "/projects/meraqui":{
    title:"Gillco Meraqui | Sector 126, Mohali",
    description:"An upcoming residential masterpiece in Sector 126, Mohali featuring 444 exquisitely designed 3 & 4 BHK luxury residences."
  },
  "/projects/parkhills":{
    title:"Gillco Parkhills | Airport Road, Mohali",
    description:"Mohali's finest and most sought after S + 19-story high Premium 2, 3, and 4 BHK apartments on Airport Road, Mohali."
  },
  "/projects/palms":{
   title:"Gillco Palms | Kharar-Landran Road, Mohali",
   description:"Nestled on Kharar-Landran Road, Mohali featuring premium 2 BHK and 3 BHK living spaces designed to blend comfort and elegance."
  },
  "/projects/villas":{
   title:"Gillco Villas | Gillco Valley",
   description:"Artistically designed 200 Duplex Villas in Gillco Valley for those who value independent lifestyle in community environment."
  },
  "/projects/floors":{
  title:"Gillco Floors | Sector 127, Mohali",
  description:"Experience the comfort of independent living with thoughtfully designed Ground + 2, 3BHK Floors that offer privacy and space."
  },
  "/projects/towers":{
    title:"Gillco Towers | Sector 127, Mohali",
    description:"Gillco Towers brought together spacious living and contemporary design through thoughtfully crafted 3 & 4 BHK residences."
  },
  "/projects/heights":{
   title:"Gillco Heights | Sector 127, Mohali",
   description:"Envisioned as a thoughtfully planned residential community offering spacious 2 & 3 BHK apartments designed for modern family living."
  },
  "/projects/industrial-park":{
    title:"Gillco Industrial Park | Airport Road, Mohali",
    description:"200 acres of development on Airport Road, Mohali, Gillco Industrial Park is a game-changer in the business and industrial landscape."
  },

  "/spotlight":{
    title:"Press & Media | Gillco Group",
    description:"Discover the stories, achievements, and milestones that continue to shape Gillco Group's legacy of excellence and innovation."
  },
  "/culture/humans":{
    title:"Humans of Gillco | Gillco Group",
    description:"Explore the inspiring stories, experiences and achievements of the individuals who call Gillco their Home."
  },
  "/culture/csr":{
    title:"Corporate Social Responsibility | Gillco Group",
    description:"Discover Gillco Group's CSR initiatives focused on community development, education, environmental responsibility, and creating meaningful social impact."
  },
  "/careers":{
    title:"Careers at Gillco Group | Gillco Group",
    description:"Join Gillco Group and build a rewarding career in an environment that values talent, learning, leadership, and professional growth."
  },
  "/contact":{
    title:"Connect With Us | Gillco Group",
    description:"Reach out to Gillco Group for sales inquiries, customer assistance, business partnerships, career opportunities, and more."
  }
};

const matchMeta = (pathname) => {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];

  if (pathname.startsWith("/projects/")) {
    const slug = pathname.replace("/projects/", "").replace(/-/g, " ");
    const title = slug.replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      title: `${title} — ${SITE_NAME}`,
      description: `${title} by Gillco Group — premium real estate development in Punjab.`,
    };
  }

  return {
    title: SITE_NAME,
    description: DEFAULT_DESC,
  };
};

const setMeta = (selector, attr, key, content) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setCanonical = (href) => {
  let tag = document.head.querySelector('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }

  tag.setAttribute("href", href);
};

const SEO = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = matchMeta(pathname);
    const image = meta.image || DEFAULT_IMAGE;
    const url = `${SITE_URL}${pathname}`;

    document.title = meta.title;

    setMeta(
      'meta[name="description"]',
      "name",
      "description",
      meta.description
    );

    setMeta(
      'meta[property="og:title"]',
      "property",
      "og:title",
      meta.title
    );

    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      meta.description
    );

    setMeta(
      'meta[property="og:type"]',
      "property",
      "og:type",
      "website"
    );

    setMeta(
      'meta[property="og:image"]',
      "property",
      "og:image",
      image
    );

    setMeta(
      'meta[property="og:url"]',
      "property",
      "og:url",
      url
    );

    setMeta(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      SITE_NAME
    );

    setMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image"
    );

    setMeta(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
      meta.title
    );

    setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      meta.description
    );

    setMeta(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      image
    );

    setCanonical(url);
  }, [pathname]);

  return null;
};

export default SEO;